import React from "react"

import type { GridFilterItem, GridFilterModel } from "@mui/x-data-grid-pro"

import { withPreservedFilterItemOrder } from "../filter-items"

export type StringFilterOperator =
  | "contains"
  | "doesNotContain"
  | "endsWith"
  | "equals"
  | "isEmpty"
  | "isNotEmpty"
  | "startsWith"

interface FilterCriteria<TSupportedOperator extends string> {
  operator: TSupportedOperator
  value?: string
}

interface FieldFilterCriteria<
  TField extends string,
  TSupportedOperator extends string,
> extends FilterCriteria<TSupportedOperator> {
  field: TField
}

const DEFAULT_OPERATORS_WITHOUT_VALUE: StringFilterOperator[] = [
  "isEmpty",
  "isNotEmpty",
]

export interface StringFilterOperatorKey {
  /** Query value for an operator that takes no text. Defaults to `true`. */
  flagValue?: boolean
  operator: StringFilterOperator
  suffix: string
}

interface UseServerMappedStringFiltersOptions<TField extends string> {
  debounceMs?: number
  fieldToApiField: Record<TField, string>
  fields: readonly TField[]
  /** Filter model restored before the first paint, for example from the list URL. */
  initialFilterModel?: GridFilterModel
  mode?: "single" | "multiple"
  operatorKeys?: readonly StringFilterOperatorKey[]
  operatorsWithoutValue?: StringFilterOperator[]
  orFilterKey?: string
}

interface UseServerMappedStringFiltersValue<TField extends string = string> {
  criteria: FieldFilterCriteria<TField, StringFilterOperator>[]
  filterModel: GridFilterModel
  filterSignature: string
  logicOperator: string
  /** Sets the live and applied models together, for restoring from the list address. */
  replaceFilterModel: (next: GridFilterModel) => void
  setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>
  whereClause: Record<string, unknown>
}
export const useServerMappedStringFilters = <TField extends string>({
  debounceMs = 300,
  fieldToApiField,
  fields,
  initialFilterModel,
  mode = "single",
  operatorKeys = STRING_FILTER_URL_OPERATORS,
  operatorsWithoutValue = DEFAULT_OPERATORS_WITHOUT_VALUE,
  orFilterKey,
}: UseServerMappedStringFiltersOptions<TField>): UseServerMappedStringFiltersValue<TField> => {
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(
    () => initialFilterModel ?? { items: [] }
  )
  const setOrderedFilterModel: React.Dispatch<
    React.SetStateAction<GridFilterModel>
  > = (update) => {
    setFilterModel((current) => {
      const next = typeof update === "function" ? update(current) : update
      return withPreservedFilterItemOrder(current, next)
    })
  }
  const [debouncedFilterModel, setDebouncedFilterModel] =
    React.useState(filterModel)
  const replaceFilterModel = React.useCallback((next: GridFilterModel) => {
    setFilterModel((current) => withPreservedFilterItemOrder(current, next))
    setDebouncedFilterModel((current) =>
      withPreservedFilterItemOrder(current, next)
    )
  }, [])
  const allowedFields = React.useMemo(() => new Set(fields), [fields])

  React.useEffect(() => {
    if (debounceMs <= 0) {
      return
    }

    const timeoutId = globalThis.setTimeout(() => {
      setDebouncedFilterModel(filterModel)
    }, debounceMs)

    return () => {
      globalThis.clearTimeout(timeoutId)
    }
  }, [debounceMs, filterModel])

  const activeFilterModel = debounceMs <= 0 ? filterModel : debouncedFilterModel

  const criteria = React.useMemo(() => {
    const isSupportedField = (field: string | undefined): field is TField =>
      typeof field === "string" && allowedFields.has(field as TField)

    const validItems = activeFilterModel.items
      .filter(
        (
          item
        ): item is typeof item & {
          field: TField
          operator: StringFilterOperator
        } =>
          isSupportedField(item.field) && isStringFilterOperator(item.operator)
      )
      .flatMap<FieldFilterCriteria<TField, StringFilterOperator>>((item) => {
        if (operatorsWithoutValue.includes(item.operator)) {
          return [{ field: item.field, operator: item.operator }]
        }

        if (typeof item.value !== "string") {
          return []
        }

        const normalizedValue = item.value.trim()
        if (normalizedValue.length === 0) {
          return []
        }

        return [
          {
            field: item.field,
            operator: item.operator,
            value: normalizedValue,
          },
        ]
      })

    return mode === "single" ? validItems.slice(0, 1) : validItems
  }, [activeFilterModel.items, allowedFields, mode, operatorsWithoutValue])
  const logicOperator = React.useMemo(
    () => (activeFilterModel.logicOperator ?? "and").toLowerCase(),
    [activeFilterModel.logicOperator]
  )

  const whereClause = React.useMemo<Record<string, unknown>>(() => {
    if (
      mode === "multiple" &&
      logicOperator === "or" &&
      orFilterKey &&
      criteria.length > 1
    ) {
      const orClauses = criteria.reduce<
        Record<string, Record<string, string | boolean>>
      >((acc, item, index) => {
        const apiField = fieldToApiField[item.field]
        acc[String(index)] = toStringFilterWhereClause(
          apiField,
          item,
          operatorKeys
        )
        return acc
      }, {})
      return { [orFilterKey]: orClauses }
    }

    return criteria.reduce<Record<string, unknown>>((acc, item) => {
      const apiField = fieldToApiField[item.field]
      return {
        ...acc,
        ...toStringFilterWhereClause(apiField, item, operatorKeys),
      }
    }, {})
  }, [
    criteria,
    fieldToApiField,
    logicOperator,
    mode,
    operatorKeys,
    orFilterKey,
  ])

  const filterSignature = React.useMemo(() => {
    if (mode === "single") {
      return JSON.stringify(criteria[0] ?? null)
    }
    return JSON.stringify({ criteria, logicOperator })
  }, [criteria, logicOperator, mode])

  return {
    criteria,
    filterModel,
    filterSignature,
    logicOperator,
    replaceFilterModel,
    setFilterModel: setOrderedFilterModel,
    whereClause,
  }
}

const isStringFilterOperator = (
  operator: string | undefined
): operator is StringFilterOperator => {
  return (
    operator === "contains" ||
    operator === "doesNotContain" ||
    operator === "endsWith" ||
    operator === "equals" ||
    operator === "isEmpty" ||
    operator === "isNotEmpty" ||
    operator === "startsWith"
  )
}

const STRING_FILTER_URL_OPERATORS = [
  { operator: "contains", suffix: "" },
  { operator: "equals", suffix: "_equals" },
  { operator: "startsWith", suffix: "_starts_with" },
  { operator: "endsWith", suffix: "_ends_with" },
  { operator: "doesNotContain", suffix: "_not_contains" },
  { operator: "isEmpty", suffix: "_empty" },
  { operator: "isNotEmpty", suffix: "_present" },
] as const satisfies readonly StringFilterOperatorKey[]

/**
 * Core API bills text filters. Contains is `<field>_contains`. Empty and
 * not-empty share `<field>_empty` (`true` is empty, `false` is present).
 */
export const CORE_API_STRING_FILTER_OPERATOR_KEYS = [
  { operator: "contains", suffix: "_contains" },
  { operator: "equals", suffix: "_equals" },
  { operator: "startsWith", suffix: "_starts_with" },
  { operator: "endsWith", suffix: "_ends_with" },
  { operator: "doesNotContain", suffix: "_not_contains" },
  { flagValue: true, operator: "isEmpty", suffix: "_empty" },
  { flagValue: false, operator: "isNotEmpty", suffix: "_empty" },
] as const satisfies readonly StringFilterOperatorKey[]

const isValuelessStringFilterOperator = (
  operator: StringFilterOperator
): boolean => operator === "isEmpty" || operator === "isNotEmpty"

const toFlagQueryValue = (flagValue: boolean | undefined): "false" | "true" =>
  flagValue === false ? "false" : "true"

export const toStringFilterWhereClause = (
  field: string,
  criteria: FilterCriteria<StringFilterOperator> | undefined,
  operatorKeys: readonly StringFilterOperatorKey[] = STRING_FILTER_URL_OPERATORS
): Record<string, string | boolean> => {
  if (!criteria) {
    return {}
  }

  const mapping = operatorKeys.find(
    (entry) => entry.operator === criteria.operator
  )
  if (!mapping) {
    return {}
  }

  const key = `${field}${mapping.suffix}`

  if (isValuelessStringFilterOperator(criteria.operator)) {
    return { [key]: mapping.flagValue ?? true }
  }

  return criteria.value ? { [key]: criteria.value } : {}
}

export const stringFilterItemsFromUrlValues = (
  field: string,
  values: Record<string, string | null>,
  operatorKeys: readonly StringFilterOperatorKey[] = STRING_FILTER_URL_OPERATORS
): GridFilterItem[] =>
  operatorKeys.flatMap(({ flagValue, operator, suffix }) => {
    const raw = values[`${field}${suffix}`]
    if (raw == null || raw === "") {
      return []
    }

    if (isValuelessStringFilterOperator(operator)) {
      if (raw !== toFlagQueryValue(flagValue)) {
        return []
      }

      return [{ field, id: `${field}-${operator}`, operator }]
    }

    return [{ field, id: `${field}-${operator}`, operator, value: raw }]
  })
